<script lang="ts">
  import { page } from '$app/stores';
  import ProductCard from '$components/product-card/product-card.svelte';
  import Skeleton from '$components/ui/skeleton.svelte';
  import type { LanguageCode } from '$gql/storefront/schemas';
  import { optimalUSCountry, parseCountryAsCode } from '$lib/country';
  import { t } from '$lib/i18n';
  import { lang } from '$lib/lang';
  import { getNostoRecomendations } from '$lib/nosto';
  import { region } from '$lib/region';
  import { shopify } from '$lib/shopify';
  import { createQuery } from '@tanstack/svelte-query';

  export let productId: string;
  export let cartItemsIds: string[] = [];

  /* GraphQL */ `
    query getCartRecommendations($productId: ID!, $lang: LanguageCode, $country: CountryCode)
    @inContext(language: $lang, country: $country) {
      productRecommendations(productId: $productId) {
        ...ProductCard
        ...QuickAddToCart
      }
    }
  `;

  const recommendationsType = $page.data.configuration.content.recommendations_type;

  $: recommendations = createQuery({
    staleTime: 5 * 60 * 1000,
    queryKey: ['cart-recommendations', productId, $lang],
    queryFn: async () => {
      return await $shopify.getCartRecommendations({
        productId: productId,
        lang: $lang.toUpperCase() as LanguageCode,
        country: parseCountryAsCode($optimalUSCountry)
      });
    }
  });

  $: nostoRecommendations = createQuery({
    staleTime: 5 * 60 * 1000,
    enabled: !!productId && recommendationsType === 'nosto',
    queryKey: ['nosto-recommendations', cartItemsIds, recommendationsType === 'nosto'],
    queryFn: async () => {
      const relatedRecommendations = await getNostoRecomendations({
        productsId: cartItemsIds || [],
        region: $region
      });
      const relatedProducts = relatedRecommendations?.recos?.related?.primary;
      const relatedProductsShopifyData = relatedProducts.map((item: { url: string }) => {
        // Get each product's Shopify data
        const product = $shopify.getQuickAddToCartProduct({
          handle: `${item.url.split('/').pop()}`,
          lang: $lang.toUpperCase() as LanguageCode,
          country: parseCountryAsCode($optimalUSCountry)
        });

        return product;
      });

      const recos = await Promise.all(relatedProductsShopifyData);
      return recos.map((item) => item.product);
    }
  });

  $: arr =
    recommendationsType === 'nosto'
      ? $nostoRecommendations.data || []
      : $recommendations.data?.productRecommendations || [];
  $: products = arr.filter(Boolean) as NonNullable<(typeof arr)[number]>[];
  $: allowedProducts = products.filter((p) => !cartItemsIds.includes(p.id)).slice(0, 3);
</script>

{#if !$recommendations.isFetched || allowedProducts.length > 0}
  <div class="mt-12 mb-1">
    <h3 class="px-2 text-xl font-semibold mb-4">
      {$t('cart.recommendations')}
    </h3>
    <div class="flex flex-col gap-2">
      {#if $recommendations.isLoading}
        {#each Array(3) as _}
          <Skeleton class="rounded-sm aspect-[332/480]" />
        {/each}
      {:else}
        {#each allowedProducts as product (product.id)}
          <ProductCard {product} variant="highlight" />
        {/each}
      {/if}
    </div>
  </div>
{/if}
